import { Button, Tooltip } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useCreateFromItem } from '../components/use-create-from-item';
import { useCreateHrefs } from '../components/use-create-hrefs';
import { CREATE_PERMISSION } from '../components/create-permissions';
import type { DataIndexItemRef } from '../utils/item';
import type { DataIndexUsageKey } from '../utils/types';
import { usageEmptyAction } from '../utils/usage-empty-actions';

type UsageEmptyActionButtonProps = {
  surface: DataIndexUsageKey;
  workspaceId: number;
  item: DataIndexItemRef;
  isBuilder: boolean;
};

/**
 * The button under an empty usage surface: make one of these from the item.
 * A write runs through the same path as the "Use in" menu, which opens the
 * new record in a tab, as the page this replaces does.
 */
export function UsageEmptyActionButton({ surface, workspaceId, item, isBuilder }: UsageEmptyActionButtonProps) {
  const { hasPermission } = usePermissions();
  const create = useCreateFromItem(workspaceId, item);
  const hrefs = useCreateHrefs(workspaceId, item.id);
  const action = usageEmptyAction(surface, {
    kind: item.kind,
    isBuilder,
    can: (target) => hasPermission(CREATE_PERMISSION[target]),
    hrefs,
  });

  if (action == null) {
    return null;
  }

  return (
    <Tooltip isDisabled={action.tooltip == null} content={action.tooltip} isCustom={true}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="secondary"
          icon="exit"
          iconPosition="trailing"
          isDisabled={action.isDisabled || create.pending != null}
          isLoading={action.href == null && create.pending === action.target}
          href={action.href}
          onClick={action.href == null ? () => void create.run(action.target) : undefined}
        >
          {action.label}
        </Button>
      )}
    </Tooltip>
  );
}
